import React, { useEffect, useState, createContext, useMemo } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useNavigation,
} from "react-router-dom";
import User from "./Pages/User/User";
import Guide from "./Pages/Guide/Guide";
import RootPage from "./Pages/RootPage";
import Infrastructure from "./Pages/Infrastructure/Infrastructure";
import Diagnostic from "./Pages/Diagnostic/Diagnostic";
import Ticket from "./Pages/Ticket/Ticket";
import Companies from "./Pages/Companies/Companies";
import Enquiries from "./Pages/Enquires/Enquries";
import Login from "./Pages/Login";
import Admin from "./Pages/Admin/Admin";
import AppConfig from "./Pages/Admin/AppConfig";
import { redirect } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import AuthWrapper from "./Core/util/AuthWrapper";
import Partners from "./Pages/Partners/Partners";
import { ActiveCompanies } from "./Pages/Companies/ActiveCompanies";
import { Activeusers } from "./Pages/User/Activeusers";
import DeletionRequests from "./Pages/User/DeletionRequests";
import Logs from "./Pages/Logs/Logs";
import Subscriptions from "./Pages/Subscriptions/Subscriptions";
import { SocketConnections } from "./Pages/SocketConnections/SocketConnections";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import axios from "axios";
import ipconfig from "./Core/ipconfig";
import { encryptData, decryptData } from "./Core/util/obfuscate";
import ErrorBoundary from "./Shared/Widgets/ErrorBoundary";

// Global response interceptor for auto-synchronizing permissions on 403 Forbidden errors
let isSyncing403 = false; // Debounce flag to prevent concurrent /me calls during 403 handling
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 403) {
      const token = localStorage.getItem("token");
      if (token && !isSyncing403) {
        isSyncing403 = true;
        try {
          const res = await axios.get(`${ipconfig}/adminpanel/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (res.data?.status) {
            const freshData = res.data.data;
            const cachedRaw = localStorage.getItem("adminMe");
            const cachedData = cachedRaw ? decryptData(cachedRaw) : null;

            // Compare fresh permissions with cached — only reload if they actually changed
            const freshPerms = JSON.stringify(freshData?.permissions || freshData?.permission || {});
            const cachedPerms = JSON.stringify(cachedData?.permissions || cachedData?.permission || {});

            if (freshPerms !== cachedPerms) {
              // Permissions changed on the server — store new data and reload to apply
              localStorage.setItem("adminMe", encryptData(freshData));
              isSyncing403 = false;
              window.location.reload();
              return new Promise(() => {}); // Halt further execution during reload
            }
            // Permissions are the same — the 403 is legitimate (admin genuinely lacks access)
            // Don't reload, just let the error propagate normally
          }
        } catch (meError) {
          console.error("Auto-syncing permissions failed on 403 response:", meError);
          localStorage.removeItem("adminMe");
          localStorage.removeItem("token");
          window.location.href = "/login";
          return new Promise(() => {});
        } finally {
          isSyncing403 = false;
        }
      }
    }
    return Promise.reject(error);
  }
);

export const ColorModeContext = createContext({ toggleColorMode: () => {}, mode: "light" });

const router = createBrowserRouter([
  // Public Routes
  {
    path: "/login",
    element: <Login />,
  },
  // Protected Routes
  {
    path: "/",
    element: (
      <AuthWrapper>
        <RootPage />
      </AuthWrapper>
    ),
    errorElement: (
      <AuthWrapper>
        <ErrorBoundary>
          <RootPage />
        </ErrorBoundary>
      </AuthWrapper>
    ),
    children: [
      { index: true, element: <User /> },
      { path: "admin", element: <Admin /> },
      { path: "appconfig", element: <AppConfig /> },
      { path: "user", element: <User /> },
      { path: "activeusers", element: <Activeusers /> },
      { path: "guide", element: <Guide /> },
      { path: "companies", element: <Companies /> },
      { path: "activecompanies", element: <ActiveCompanies /> },
      { path: "enquiry", element: <Enquiries /> },
      { path: "deletion-requests", element: <DeletionRequests /> },
      { path: "partners", element: <Partners /> },
      { path: "infrastructure", element: <Infrastructure /> },
      { path: "diagnostic", element: <Diagnostic /> },
      { path: "ticket", element: <Ticket /> },
      { path: "logs", element: <Logs /> },
      { path: "subscriptions", element: <Subscriptions /> },
      { path: "connections", element: <SocketConnections /> },
    ],
  },
]);

function App() {
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem("theme");
    return saved === "dark" || saved === "light" ? saved : "light";
  });

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((prevMode) => {
          const nextMode = prevMode === "light" ? "dark" : "light";
          localStorage.setItem("theme", nextMode);
          document.documentElement.setAttribute("data-theme", nextMode);
          return nextMode;
        });
      },
    }),
    [mode]
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === "light" ? "#0e75a1" : "#38bdf8",
          },
          background: {
            default: mode === "light" ? "#f8fafc" : "#0f172a",
            paper: mode === "light" ? "#ffffff" : "#1e293b",
          },
          text: {
            primary: mode === "light" ? "#0f172a" : "#f8fafc",
            secondary: mode === "light" ? "#475569" : "#94a3b8",
          },
          divider: mode === "light" ? "#e2e8f0" : "#334155",
        },
        typography: {
          fontFamily: [
            "Inter",
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            "sans-serif",
          ].join(","),
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: "12px",
                boxShadow:
                  mode === "light"
                    ? "0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)"
                    : "0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)",
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RouterProvider router={router} />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;

